import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Button, 
  Alert, 
  Typography, 
  Space,
  Result,
  message,
  Descriptions,
  Tag,
  Spin,
  Modal
} from 'antd';
import { 
  BankOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useAccount } from '../../context/AccountContext';
import { formatAmount } from '../../utils';

const { Title, Text } = Typography;
const { Option } = Select;

const Withdraw: React.FC = () => {
  const { accountStatus, updateAccountStatus } = useAccount();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedCard, setSelectedCard] = useState<any>(null);

  const [withdrawStatus, setWithdrawStatus] = useState<'pending' | 'processing' | 'success' | 'failed'>('pending');

  // 每次组件挂载时重置到第一步
  React.useEffect(() => {
    setCurrentStep(0);
    setWithdrawAmount('');
    setSelectedCard(null);
    setWithdrawStatus('pending');
    form.resetFields();
  }, [form]);

  // 步骤1：选择银行卡和输入金额
  const handleWithdrawSubmit = async (values: any) => {
    setLoading(true);
    try {
      // 验证余额
      const currentBalance = parseFloat(accountStatus.balance);
      const amount = parseFloat(values.amount);
      
      if (amount > currentBalance) {
        message.error(`提现金额（¥${amount.toFixed(2)}）不能超过账户余额（¥${currentBalance.toFixed(2)}）`);
        setLoading(false);
        return;
      }

      // 模拟选择银行卡
      const card = accountStatus.cardInfo?.find(c => c.id === values.cardId);
      if (!card) {
        message.error('请选择有效的银行卡');
        setLoading(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      setWithdrawAmount(values.amount);
      setSelectedCard(card);
      setCurrentStep(1);
    } catch (error) {
      message.error('提现申请失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 步骤2：确认提现信息并提交申请
  const handleConfirmWithdraw = async () => {
    // 显示二次确认弹框
    Modal.confirm({
      title: '确认提现申请',
      content: (
        <div>
          <p>请确认以下提现信息：</p>
          <p><strong>提现金额：</strong>¥{formatAmount(withdrawAmount)}</p>
          <p><strong>到账银行卡：</strong>{selectedCard?.bind_acct_bank_nme} (尾号{selectedCard?.bind_acct_no.slice(-4)})</p>
          <p style={{ color: '#faad14', marginTop: '8px' }}>
            确认提交后，提现申请将无法撤销
          </p>
        </div>
      ),
      okText: '确认提交',
      cancelText: '取消',
      okType: 'primary',
      onOk: async () => {
        setLoading(true);
        try {
          // 模拟提交提现申请
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          setWithdrawStatus('processing');
          setCurrentStep(2);
          
          // 模拟异步处理提现
          setTimeout(() => {
            // 更新账户余额（扣除提现金额）
            const currentBalance = parseFloat(accountStatus.balance);
            const withdrawAmountNum = parseFloat(withdrawAmount);
            const newBalance = currentBalance - withdrawAmountNum;
            updateAccountStatus({
              balance: newBalance.toFixed(2)
            });
            message.success('提现申请已提交，正在处理中');
          }, 1000);
        } catch (error) {
          message.error('提现申请提交失败，请重试');
        } finally {
          setLoading(false);
        }
      }
    });
  };




  // 渲染步骤1：选择银行卡和输入金额
  const renderStep1 = () => (
    <Card title="提现申请" className="step-card">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleWithdrawSubmit}
        requiredMark={false}
      >
        <Alert
          message="提现说明"
          description={
            <div>
              <p>• 提现仅支持提现到已验证的银行卡</p>
              <p>• 提现申请提交后需要等待银行处理</p>
              <p>• 提现到账时间：预计1-3个工作日，具体以银行实际处理为准</p>
              {/*<p>• 单次提现金额最低100元，最高不超过账户余额</p>*/}
              <p>• 提现无手续费</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <div style={{ 
          background: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '6px', 
          marginBottom: '24px' 
        }}>
          <Text style={{ color: '#666' }}>当前余额：</Text>
          <Text style={{ 
            fontSize: '20px', 
            fontWeight: 'bold', 
            color: '#1890ff', 
            marginLeft: '8px' 
          }}>
            ¥{formatAmount(accountStatus.balance)}
          </Text>
        </div>

        <Form.Item 
          name="cardId" 
          label="提现银行卡" 
          rules={[{ required: true, message: '请选择提现银行卡' }]}
        >
          <Select placeholder="请选择提现银行卡" size="large">
            {accountStatus.cardInfo?.filter(card => card.status === 'verified').map(card => (
              <Option key={card.id} value={card.id}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Space>
                    <BankOutlined />
                    <div>
                      <div>{card.bind_acct_bank_nme}</div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        **** **** **** {card.bind_acct_no.slice(-4)}
                      </Text>
                    </div>
                  </Space>
                  <Tag color="green">已验证</Tag>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          name="amount" 
          label="提现金额（元）" 
          rules={[
            { required: true, message: '请输入提现金额' },
            { 
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const amount = parseFloat(value);
                const balance = parseFloat(accountStatus.balance);
                
                if (isNaN(amount) || amount <= 0) {
                  return Promise.reject(new Error('请输入有效的金额'));
                }
                if (amount < 100) {
                  return Promise.reject(new Error('提现金额不能少于100元'));
                }
                if (amount > balance) {
                  return Promise.reject(new Error('提现金额不能超过账户余额'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input 
            size="large"
            placeholder="请输入提现金额" 
            prefix="¥"
            style={{ fontSize: '16px' }}
          />
        </Form.Item>

        {(!accountStatus.cardInfo || accountStatus.cardInfo.filter(card => card.status === 'verified').length === 0) && (
          <Alert
            message="暂无可用银行卡"
            description="请先添加并验证银行卡后再进行提现操作"
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
            action={
              <Button 
                size="small" 
                type="primary"
                onClick={() => window.location.href = '/account/cards'}
              >
                管理银行卡
              </Button>
            }
          />
        )}

        <Form.Item style={{ textAlign: 'center' }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large" 
            loading={loading}
            style={{ minWidth: 120 }}
            disabled={!accountStatus.cardInfo || accountStatus.cardInfo.filter(card => card.status === 'verified').length === 0}
          >
            确认提现
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  // 渲染步骤2：确认提现信息
  const renderStep2 = () => (
    <Card title="确认提现信息" className="step-card">
      <Alert
        message="请仔细核对提现信息"
        description="确认无误后点击提交提现申请"
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Descriptions 
        bordered 
        column={1} 
        size="middle"
        labelStyle={{ width: '120px', fontWeight: 'bold' }}
      >
        <Descriptions.Item label="提现金额">
          <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
            ¥{formatAmount(withdrawAmount)}
          </Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="到账金额">
          <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
            ¥{formatAmount(withdrawAmount)}
          </Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="提现银行卡">
          <div>
            <div style={{ fontWeight: 500 }}>{selectedCard?.bind_acct_bank_nme}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              **** **** **** {selectedCard?.bind_acct_no.slice(-4)}
            </Text>
          </div>
        </Descriptions.Item>
        
        <Descriptions.Item label="户名">
          张三
        </Descriptions.Item>
        
        <Descriptions.Item label="预计到账时间">
          <div>
            <div>预计1-3个工作日，<Text style={{ color: '#faad14' }}>具体以银行实际处理为准</Text></div>
          </div>
        </Descriptions.Item>
      </Descriptions>

      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Space size="large">
          <Button 
            size="large"
            onClick={() => setCurrentStep(0)}
          >
            修改信息
          </Button>
          <Button 
            type="primary" 
            size="large" 
            onClick={handleConfirmWithdraw}
            loading={loading}
            style={{ minWidth: 140 }}
          >
            提交提现申请
          </Button>
        </Space>
      </div>
    </Card>
  );

  // 渲染步骤3：处理中页面
  const renderStep3 = () => (
    <Card title="提现处理中" className="step-card">
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Space direction="vertical" size="large">
          <Spin size="large" />
          
          <Title level={3}>
            <ClockCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            提现申请处理中
          </Title>
          
          <Alert
            message="提现申请已提交"
            description="您的提现申请正在处理中，请耐心等待银行处理结果"
            type="info"
            showIcon
          />
          
          <Descriptions column={1} size="middle" style={{ maxWidth: 400, margin: '0 auto' }}>
            <Descriptions.Item label="提现金额">
              <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                ¥{formatAmount(withdrawAmount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="处理状态">
              <Tag color="processing">处理中</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="预计到账时间">
              <div>
                <div>预计1-3个工作日，<Text style={{ color: '#faad14' }}>具体以银行实际处理为准</Text></div>
              </div>
            </Descriptions.Item>
          </Descriptions>
          

        </Space>
      </div>
    </Card>
  );

  // 渲染步骤4：处理完成
  const renderStep4 = () => (
    <Card className="step-card">
      <Result
        status={withdrawStatus === 'success' ? 'success' : 'error'}
        title={withdrawStatus === 'success' ? '提现成功！' : '提现失败'}
        subTitle={
          withdrawStatus === 'success' 
            ? <span>提现金额 ¥{formatAmount(withdrawAmount)} 已成功提交，预计1-3个工作日内到达您的银行卡，<Text style={{ color: '#faad14' }}>具体以银行实际处理为准</Text></span>
            : '提现申请处理失败，请联系客服处理'
        }
        extra={[
          <div key="info" style={{ marginBottom: 24 }}>
            <Descriptions column={1} size="small">

              <Descriptions.Item label="当前余额">
                <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                  ¥{formatAmount(accountStatus.balance)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </div>,
          <Button 
            key="back-to-account" 
            type="primary" 
            onClick={() => window.location.href = '/account/center'}
          >
            返回账户中心
          </Button>
        ]}
      />
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div style={{ maxWidth: 800, margin: '16px auto' }}>


        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default Withdraw;